import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ArrowDown, Check } from "lucide-react";
import { SectionClouds } from "./SectionClouds";

type Audience = "startup" | "scaleup" | "prototype";

const audienceOrder: Audience[] = ["startup", "scaleup", "prototype"];

interface Group {
  titleLead: string;
  titleEm: string;
  tag: string;
  summary: string;
  desc: string;
  bullets: string[];
}

export function Audiences() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const selectAudience = (audience: Audience) => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem("holymoly:via", audience);
        window.dispatchEvent(new CustomEvent("holymoly:via", { detail: audience }));
      } catch {
        // ignore
      }
    }
    // See Process.tsx's handleSelect for why this goes through the router's
    // navigate() rather than a raw `window.history.replaceState`: a raw call
    // still notifies the router (its history is monkey-patched) and, with
    // scrollRestoration: true, can re-scroll to a stale hash left in the URL.
    // `resetScroll: false` + dropping the hash avoids that; the explicit
    // scrollIntoView below is the *only* scroll we want from this click.
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, via: audience }),
      hash: "",
      replace: true,
      resetScroll: false,
      hashScrollIntoView: false,
    });
    const el = document.querySelector("#process");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} id="audiences" className="frame bg-page-bg">
      <SectionClouds variant="audiences" />
      <div className="container-narrow section-pad relative z-10">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">01 —</span> {t("landing.audiences.eyebrow")}
          </span>
          <h2 className="section-title">
            <Trans
              i18nKey="landing.audiences.title"
              components={{ italic: <span className="serif-italic" /> }}
            />
          </h2>
          <p className="section-lede">{t("landing.audiences.lede")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-7">
          {audienceOrder.map((audience, i) => {
            const g = t(`landing.audiences.groups.${audience}`, {
              returnObjects: true,
            }) as Group;
            return (
              <button
                key={audience}
                type="button"
                onClick={() => selectAudience(audience)}
                className={cn(
                  "lift relative flex flex-col rounded-[22px] border border-line bg-surface p-8 text-left sm:p-9",
                  "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-page-bg",
                  "transition-all duration-700",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
                )}
                style={{ transitionDelay: isVisible ? `${i * 80}ms` : "0ms" }}
              >
                <div className="mono text-[11px] tracking-[0.14em] text-ink-3 uppercase">
                  {g.tag}
                </div>

                <h3 className="mt-3 text-[clamp(28px,3vw,38px)] leading-[1.05] font-medium tracking-[-0.03em] text-ink">
                  {g.titleLead} <span className="serif-italic text-accent">{g.titleEm}</span>
                </h3>

                <div className="mt-3 text-[15px] leading-snug font-medium text-ink">
                  {g.summary}
                </div>

                <p className="mt-3 text-[14px] leading-[1.6] text-ink-2">{g.desc}</p>

                <ul className="mt-5 space-y-2.5">
                  {g.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-[13.5px] text-ink-2">
                      <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-accent/40 text-accent">
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mono mt-6 inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] text-accent uppercase">
                  <span>{t("landing.audiences.seeProcess")}</span>
                  <ArrowDown className="h-3 w-3" strokeWidth={2.5} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
